import axios from 'axios';

const baseURL =
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:5000/api/v1';

const axiosInstance = axios.create({
  baseURL,
});

axiosInstance.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('accessToken');

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

let isRefreshing = false;
let pendingRequests = [];

function resolvePendingRequests(newAccessToken) {
  pendingRequests.forEach((callback) => callback(newAccessToken));
  pendingRequests = [];
}

function rejectPendingRequests(error) {
  pendingRequests.forEach((callback) => callback(null, error));
  pendingRequests = [];
}

axiosInstance.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';

    if (!originalRequest) {
      return Promise.reject(error);
    }

    /*
     * درخواست‌های عمومی نباید باعث تلاش برای refresh یا ریدایرکت شوند.
     */
    const isPublicRequest =
      requestUrl.startsWith('/public/') ||
      requestUrl.includes('/public/');

    /*
     * فقط مسیرهایی که خودشان بخشی از چرخه‌ی احراز هویت/تازه‌سازی هستند
     * (login, refresh, logout) باید از منطق auto-refresh مستثنی شوند.
     * توجه: "/auth/me" را اینجا استثنا نمی‌کنیم — این endpoint هم مثل هر
     * درخواست محافظت‌شده‌ی دیگر باید در صورت 401 (انقضای accessToken)
     * از refreshToken برای گرفتن توکن جدید استفاده کند، وگرنه کاربر
     * به‌طور بی‌صدا (بدون خطا، بدون ریدایرکت) خارج‌شده تلقی می‌شود.
     */
    const isNonRetryableAuthRequest =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/logout');

    if (
      status === 401 &&
      !isPublicRequest &&
      !isNonRetryableAuthRequest &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push((newAccessToken, refreshError) => {
            if (refreshError || !newAccessToken) {
              reject(refreshError || error);
              return;
            }

            originalRequest.headers.Authorization =
              `Bearer ${newAccessToken}`;

            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken =
          localStorage.getItem('refreshToken');

        if (!refreshToken) {
          throw new Error('NO_REFRESH_TOKEN');
        }

        const response = await axios.post(
          `${baseURL}/auth/refresh`,
          { refreshToken }
        );

        const {
          accessToken,
          refreshToken: newRefreshToken,
        } = response.data.data;

        localStorage.setItem(
          'accessToken',
          accessToken
        );

        if (newRefreshToken) {
          localStorage.setItem(
            'refreshToken',
            newRefreshToken
          );
        }

        resolvePendingRequests(accessToken);

        originalRequest.headers.Authorization =
          `Bearer ${accessToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        rejectPendingRequests(refreshError);

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
